import { useQuery } from "@tanstack/react-query";
import { PROOFING_FLOW_APP_ID } from "@/config";
import type { ProjectsGetProjectByIdResponses } from "@/usersAndProjects-client";
import {
  edifyPackagesGetProjectByPackageId,
  projectsGetProjectById,
  projectsGetProjectEntities,
} from "@/usersAndProjects-client/sdk.gen";
import type { ProjectByPackage, ProjectWithRole } from "../entities";

/**
 * Fetch project ID by package ID
 */
export interface UseFetchProjectByPackageResult {
  project: ProjectByPackage | null;
  isLoading: boolean;
  error: Error | null;
}

export const useFetchProjectByPackage = (
  packageId: string | null,
): UseFetchProjectByPackageResult => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["project-by-package", packageId],
    queryFn: async () => {
      if (!packageId) {
        throw new Error("Package ID is required");
      }
      const response = await edifyPackagesGetProjectByPackageId({
        path: { packageId },
      });
      // Validate response is an object with expected structure, not HTML error page
      if (!response.data || typeof response.data !== "object" || !("project" in response.data)) {
        console.warn("Invalid project response format:", response.data);
        return null;
      }
      return response.data;
    },
    enabled: !!packageId,
    retry: false, // Don't retry on HTML error responses
  });

  return {
    project: data || null,
    isLoading,
    error: error as Error | null,
  };
};

/**
 * Fetch project with role information
 */
export interface UseFetchProjectWithRoleResult {
  projectData: ProjectWithRole | null;
  isLoading: boolean;
  error: Error | null;
}

export const useFetchProjectWithRole = (
  projectId: string | null,
  roleName: string,
): UseFetchProjectWithRoleResult => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["project-with-role", projectId, roleName],
    queryFn: async () => {
      if (!projectId) {
        throw new Error("Project ID is required");
      }
      const response = await projectsGetProjectEntities({
        path: { projectId, roleName },
        query: { appId: PROOFING_FLOW_APP_ID },
      });
      return response.data;
    },
    enabled: !!projectId && !!roleName,
  });

  return {
    projectData: data || null,
    isLoading,
    error: error as Error | null,
  };
};

/**
 * Fetch full project details including assigned users with roles
 */
export interface UseFetchProjectDetailsResult {
  projectDetails: ProjectsGetProjectByIdResponses[200] | null;
  isLoading: boolean;
  error: Error | null;
}

export const useFetchProjectDetails = (
  projectId: string | null | undefined,
): UseFetchProjectDetailsResult => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["project-details", projectId],
    queryFn: async () => {
      if (!projectId) {
        throw new Error("Project ID is required");
      }
      const response = await projectsGetProjectById({
        path: { id: projectId },
      });
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });

  return {
    projectDetails: data || null,
    isLoading,
    error: error as Error | null,
  };
};
